import React, { useRef, useCallback, useMemo, useEffect, useState, useContext, Fragment } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import { formatDate } from "common/calendar";
import serveRequestErrors from "common/serve-request-errors";
import { NOTIFICATION_REFERENCE_PARAM } from "common/notification-routes";
import { AxiosContext } from "contexts/with-interceptor-provider";
import AppStatus from "components/app-status";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell, AppTableEditIcon } from "components/app-table";
import AppHistoryLeaveModal from "components/pages/leave-management/app-history-leave-modal";
import pdfIcon from "assets/images/pdf-icon.svg";
import viewIcon from "assets/images/view-icon.svg";

const AppLeaveHistory = () => {
	const { id } = useParams();
	const leaveModalRef = useRef();
	const context = useContext(AxiosContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({ statusListStr: searchParams.get("statusListStr") || "CANCELLED,APPROVED,REJECTED", page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "", tab: "Leave History" });
	const notificationRefId = useRef(searchParams.get(NOTIFICATION_REFERENCE_PARAM) || "");
	const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status, [data.status]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.leaves.managerHistoryList(id)(sanitizeObject({ ...payload, id }));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, [id]);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	const onHandleView = (obj) => {
		leaveModalRef.current.onHandleShow(obj.row.original.id);
	};

	const onHandleSort = useCallback(
		(id, order) => {
			paramsRef.current.sort = order ? id + "," + order : "";

			paramsRef.current.page = 1;

			onHandleGetList();
		},
		[onHandleGetList]
	);

	//prettier-ignore
	const MenuCell = useCallback(( obj ) => {
		return <AppTableEditIcon icon={viewIcon} onClick={(event) => onHandleView(obj)} />;
	}, []);

	const StartDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row.original.companyLeaveApplicationDetails[0].date) || "-"} />, []);

	const EndDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row?.original?.companyLeaveApplicationDetails?.[row?.original?.companyLeaveApplicationDetails?.length - 1]?.date) || "-"} />, []);

	const StatusCell = useCallback(({ row }) => <AppStatus status={row.values.status} />, []);

	//prettier-ignore
	const AttachmentCell = useCallback(({ row }) => {
		const file = row.original?.companyLeaveApplicationFiles?.[0];
		return (<AppTableCell center value={file ? (<a href={file?.fileURL} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attacment" /></a>) : ("-")} />);
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Leave Type",
			accessor: "leaveType",
			disableSortBy: false,
		},
		{
			Header: "Days",
			accessor: "noOfDays",
			disableSortBy: false,
		},
		{
			Header: "Start Date",
			id: "startDate",
			accessor: "companyLeaveApplicationDetails",
			disableSortBy: false,
            Cell: StartDateCell
		},
		{
			Header: "End Date",
			id: "endDate",
			accessor: "companyLeaveApplicationDetails",
			disableSortBy: false,
            Cell: EndDateCell
		},
		{
            Header: "Time Off",
			accessor: "timeOff.label",
			disableSortBy: false,
		},
		{
            Header: () => <div className="table__attachment">Attachment</div>,
			accessor: "companyLeaveApplicationFiles",
			disableSortBy: true,
			Cell: AttachmentCell
		},
		{
			Header: "Status",
			accessor: "status",
			disableSortBy: false,
			Cell: StatusCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuCell,
		},
	], [MenuCell, StatusCell, StartDateCell, EndDateCell, AttachmentCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	// opens the detail modal when arriving from a notification, then strips the
	// param so the same notification can be clicked again.
	useEffect(() => {
		const referenceId = searchParams.get(NOTIFICATION_REFERENCE_PARAM) || notificationRefId.current;

		if (!referenceId) return;

		notificationRefId.current = "";

		leaveModalRef.current?.onHandleShow(referenceId);

		memoSetSearchParams.current(sanitizeObject({ ...paramsRef.current, size: 10 }), { replace: true });
	}, [searchParams]);

	useEffect(() => {
		return () => {
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.LEAVES.MANAGER_HISTORY_LIST);
		};
	}, [context]);

	return (
		<Fragment>
			{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}

			{!isEmptyState && <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />}

			<AppHistoryLeaveModal ref={leaveModalRef} />
		</Fragment>
	);
};

export default AppLeaveHistory;
